/**
 * Analytics helper utilities for debouncing and throttling tracking events.
 * Prevents event spam from rapid user interactions (filter clicks, toggles, etc.)
 */

/**
 * Calculates session duration in seconds from a given start timestamp (wall clock).
 * Returns 0 if no start time is provided.
 * Note: Prefer createActiveSessionTimer() for accurate active-only tracking.
 *
 * @param {number|null} startTime - Timestamp captured via Date.now()
 * @returns {number} Elapsed duration in whole seconds
 */
export function calcSessionDuration(startTime) {
  if (!startTime) return 0
  return Math.round((Date.now() - startTime) / 1000)
}

/**
 * Creates a timer that tracks only the time the browser tab is actively visible.
 * Uses the Page Visibility API (visibilitychange) to pause counting when the
 * user switches tabs or minimizes the window, and resumes when they return.
 *
 * This prevents inflated session_duration values caused by idle/background tabs
 * (e.g., a healthcare worker switching to another app mid-session).
 *
 * Usage:
 *   const timer = createActiveSessionTimer()
 *   timer.start()
 *   // ... user interacts ...
 *   const seconds = timer.getSeconds()  // accumulated active time
 *   timer.stop()                        // cleanup listener + return seconds
 *
 * @returns {{ start: Function, getSeconds: Function, stop: Function }}
 */
export function createActiveSessionTimer() {
  let accumulatedMs = 0
  let lastResumeTime = null
  let listening = false

  function onVisibilityChange() {
    if (document.hidden) {
      // Tab hidden → freeze the stopwatch
      if (lastResumeTime) {
        accumulatedMs += Date.now() - lastResumeTime
        lastResumeTime = null
      }
    } else {
      // Tab visible again → resume the stopwatch
      lastResumeTime = Date.now()
    }
  }

  return {
    /** Begin tracking active time. */
    start() {
      accumulatedMs = 0
      lastResumeTime = document.hidden ? null : Date.now()
      if (!listening) {
        document.addEventListener('visibilitychange', onVisibilityChange)
        listening = true
      }
    },

    /** Returns accumulated active seconds so far (non-destructive). */
    getSeconds() {
      let total = accumulatedMs
      if (lastResumeTime) {
        total += Date.now() - lastResumeTime
      }
      return Math.round(total / 1000)
    },

    /** Stops tracking, removes listener, and returns final active seconds. */
    stop() {
      const seconds = this.getSeconds()
      document.removeEventListener('visibilitychange', onVisibilityChange)
      listening = false
      lastResumeTime = null
      return seconds
    },
  }
}

/**
 * Calculates the number of days between two date strings.
 *
 * @param {string} from - Start date string (ISO or any Date-parseable format)
 * @param {string} to - End date string
 * @returns {number} Number of whole days in the range
 */
export function calcRangeDays(from, to) {
  const rangeMs = new Date(to) - new Date(from)
  return Math.round(rangeMs / (1000 * 60 * 60 * 24))
}

/**
 * Creates a debounced version of the track function.
 * Only fires the event after the user stops triggering for `delay` ms.
 *
 * @param {Function} trackFn - The analytics track function (e.g. this.$track)
 * @param {number} delay - Debounce delay in milliseconds (default: 300)
 * @returns {Function} Debounced tracker function
 */
export function createDebouncedTracker(trackFn, delay = 300) {
  let timer = null

  return (event, props) => {
    clearTimeout(timer)
    timer = setTimeout(() => trackFn(event, props), delay)
  }
}

/**
 * Creates a throttled version of the track function.
 * Fires at most once per `interval` ms, dropping intermediate calls.
 *
 * @param {Function} trackFn - The analytics track function
 * @param {number} interval - Throttle interval in milliseconds (default: 500)
 * @returns {Function} Throttled tracker function
 */
export function createThrottledTracker(trackFn, interval = 500) {
  let lastCall = 0

  return (event, props) => {
    const now = Date.now()

    if (now - lastCall >= interval) {
      lastCall = now
      trackFn(event, props)
    }
  }
}

export function encodeBase64Unicode(value) {
  if (typeof TextEncoder !== 'function') {
    return btoa(encodeURIComponent(value).replace(/%([0-9A-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16))))
  }

  const bytes = new TextEncoder().encode(value)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/**
 * Sends a tracking event using navigator.sendBeacon for reliable delivery
 * during page unload / tab close. Falls back to storing in sessionStorage.
 *
 * @param {string} event - Event name
 * @param {object} props - Event properties
 * @param {string} mixpanelToken - Mixpanel project token
 */
export function sendBeaconEvent(event, props, mixpanelToken) {
  if (!mixpanelToken) return

  const payload = [{
    event,
    properties: {
      ...props,
      token: mixpanelToken,
      time: Math.floor(Date.now() / 1000),
      distinct_id: (props && props.user_id ? String(props.user_id) : null)
        || (() => { try { return localStorage.getItem('mixpanel_device_id') } catch (e) { return null } })()
        || 'unknown',
    },
  }]

  let data = null
  try {
    data = encodeBase64Unicode(JSON.stringify(payload))
  } catch (e) {
    return
  }
  const body = `data=${encodeURIComponent(data)}`

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/x-www-form-urlencoded' })
    const sent = navigator.sendBeacon('https://api.mixpanel.com/track?ip=1', blob)

    if (sent) return
  }

  // Fallback: store in sessionStorage for next session pickup
  try {
    const pending = JSON.parse(sessionStorage.getItem('analytics_pending_exit') || '[]')
    pending.push({ event, properties: props, timestamp: Date.now() })
    sessionStorage.setItem('analytics_pending_exit', JSON.stringify(pending))
  } catch (e) {
    // silently ignore storage errors
  }
}
